import { useTonWallet } from '@tonconnect/ui-react'
import React, {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useContactAddresses } from '../hooks/useContactAddresses'
import { useInitializeContracts } from '../hooks/useInitializeContracts'
import { useInitializeTonApiClient } from '../hooks/useInitializeTonApiClient'
import { useUserProfile } from '../hooks/useUserProfile'
import { useReferralKey } from '../hooks/useReferralKey'
import { onTokenExpired } from '../shared/utils/apiClient'
import { getAuthToken } from '../shared/utils/authToken'
import { getMockWalletState, isE2ETestMode } from '../utils/testMode'
import { type AppContextType, defaultContextValue } from './App.context.types'
import { captureReferralKey } from './App.context.utils'

export const AppContext = createContext<AppContextType>(defaultContextValue)

export const AppContextProvider: FC<
  PropsWithChildren & { language: string }
> = ({ children, language }) => {
  const contractsData = useInitializeContracts()
  const tonApiClient = useInitializeTonApiClient()
  const contractsAddresses = useContactAddresses()
  const tonConnectWallet = useTonWallet()

  // In E2E test mode, use mock wallet state if available
  const mockWallet = isE2ETestMode() ? getMockWalletState() : null
  const walletAddress = mockWallet?.connected
    ? mockWallet.address
    : (tonConnectWallet?.account.address ?? null)

  const [authenticated, setAuthenticated] = useState<boolean>(
    // In E2E test mode with mock wallet, consider authenticated
    Boolean(getAuthToken()) ||
      (isE2ETestMode() && Boolean(mockWallet?.connected)),
  )

  // Derived state: safe to make authenticated requests only when both conditions are met
  const canFetchUserData = authenticated && Boolean(walletAddress)

  // Load user profile
  const { data: userProfile, isLoading: isLoadingProfile } = useUserProfile(
    walletAddress ?? undefined,
    canFetchUserData,
  )

  // Manage referral key (auto-syncs from profile)
  const {
    referralKey,
    setReferralKey,
    isLoading: isLoadingReferralKey,
  } = useReferralKey(userProfile, isLoadingProfile)

  // Capture referral key from URL or Telegram (once on mount)
  const browserReferralKey = useMemo(() => captureReferralKey(), [])

  const basePath = '/'

  // Subscribe to token expiration events
  useEffect(() => {
    const unsubscribe = onTokenExpired(() => {
      setAuthenticated(false)
    })
    return unsubscribe
  }, [])

  return (
    <AppContext.Provider
      value={{
        language,
        client: contractsData?.client,
        wallet: walletAddress,
        sender: contractsData?.sender,
        boxCollection: contractsData?.boxCollection,
        tonApiClient,
        contractsAddresses,
        basePath,
        referralKey,
        setReferralKey,
        browserReferralKey,
        authenticated,
        canFetchUserData,
        setAuthenticated,
        userProfile,
        isLoadingProfile,
        isLoadingReferralKey,
      }}
    >
      {children}
    </AppContext.Provider>
  )
}

export const useAppContext = () => useContext(AppContext)
